import { useEffect, useState } from "react";
import { Button, Card, Col, Form, Modal, Row, Spinner } from "react-bootstrap";
import { BsEnvelope, BsGear, BsPeople } from "react-icons/bs";
import { useGetMemberships } from "../../api/queries/useGetMemberships";
import { useUpdateWelcomeEmail } from "../../api/mutations/useUpdateWelcomeEmail";
import { Memberships } from "../../types/Types";
import AdminPanelHeader from "./AdminPanelHeader";

const ManageWelcomeEmails = () => {
  const { data: memberships = [], isLoading, isError, error } = useGetMemberships();
  const updateMutation = useUpdateWelcomeEmail();

  const [selected, setSelected] = useState<Memberships | null>(null);
  const [showConfig, setShowConfig] = useState(false);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBodyHtml, setEmailBodyHtml] = useState("");
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    if (!selected) return;
    setEmailSubject(selected.welcomeEmailSubject || "");
    setEmailBodyHtml(selected.welcomeEmailBodyHtml || "");
    setActionError(null);
  }, [selected]);

  const openConfig = (membership: Memberships) => {
    setSelected(membership);
    setShowConfig(true);
  };

  const closeConfig = () => {
    setShowConfig(false);
    setSelected(null);
    setActionError(null);
  };

  const handleSave = async () => {
    if (!selected) return;
    if (!emailSubject.trim() || !emailBodyHtml.trim()) {
      setActionError("Enter both email subject and body.");
      return;
    }
    setActionError(null);
    try {
      await updateMutation.mutateAsync({
        id: selected.id,
        payload: {
          welcomeEmailSubject: emailSubject,
          welcomeEmailBodyHtml: emailBodyHtml,
        },
      });
      closeConfig();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Failed to save welcome email.");
    }
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="alert alert-danger">
        Failed to load welcome emails.
        {error instanceof Error ? ` ${error.message}` : ""}
      </div>
    );
  }

  return (
    <div className="w-100">
      <AdminPanelHeader
        icon={<BsEnvelope size={22} />}
        title="Welcome Emails"
        description="Welcome email subject and body for each membership type"
      />

      {actionError && !showConfig && (
        <div className="alert alert-danger py-2">{actionError}</div>
      )}

      <Row className="g-4">
        {memberships.map((membership) => {
          const configured = !!(
            membership.welcomeEmailSubject?.trim() &&
            membership.welcomeEmailBodyHtml?.trim()
          );
          return (
            <Col key={membership.id} md={4}>
              <Card className="h-100 shadow border-0 overflow-hidden">
                <Card.Header className="bg-project-gradient text-white border-0 py-3">
                  <div className="d-flex align-items-center justify-content-between gap-2">
                    <div className="d-flex align-items-center gap-2">
                      <span className="bg-white bg-opacity-25 rounded-circle p-2 d-inline-flex">
                        <BsPeople size={18} />
                      </span>
                      <div className="fw-bold">{membership.name}</div>
                    </div>
                    <span
                      className={`badge rounded-pill ${
                        configured ? "text-bg-success" : "text-bg-light text-dark"
                      }`}
                    >
                      {configured ? "Configured" : "Not set"}
                    </span>
                  </div>
                </Card.Header>
                <Card.Body className="bg-white">
                  <div className="d-flex align-items-center gap-3 p-2 rounded-3 bg-light border">
                    <span className="rounded-circle bg-success text-white p-2 d-inline-flex flex-shrink-0">
                      <BsEnvelope size={16} />
                    </span>
                    <div className="min-w-0">
                      <div className="small fw-semibold text-secondary">Welcome email subject</div>
                      <div className="fw-semibold text-dark text-truncate">
                        {membership.welcomeEmailSubject?.trim() || "Not set"}
                      </div>
                    </div>
                  </div>
                </Card.Body>
                <Card.Footer className="bg-white border-top">
                  <Button
                    size="sm"
                    variant="outline-primary"
                    className="w-100 d-inline-flex align-items-center justify-content-center gap-2 btn-hover-project-transparent"
                    onClick={() => openConfig(membership)}
                  >
                    <BsGear size={15} />
                    Configure
                  </Button>
                </Card.Footer>
              </Card>
            </Col>
          );
        })}
      </Row>

      <Modal show={showConfig} onHide={closeConfig} centered size="lg" backdrop="static">
        <Modal.Header closeButton closeVariant="white" className="bg-project-gradient text-white border-0">
          <Modal.Title className="fs-5 d-flex align-items-center gap-2 text-white">
            <BsGear size={20} />
            {selected?.name || "Welcome email"} — Welcome email
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-light">
          {actionError && <div className="alert alert-danger py-2 mb-3">{actionError}</div>}

          <Form>
            <Card className="mb-0 border-0 shadow-sm">
              <Card.Header className="bg-white border-bottom py-3">
                <div className="d-flex align-items-center gap-2">
                  <span className="rounded-circle bg-success text-white p-2 d-inline-flex">
                    <BsEnvelope size={14} />
                  </span>
                  <div className="fw-semibold text-dark">Welcome email</div>
                </div>
              </Card.Header>
              <Card.Body className="bg-white">
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Email subject</Form.Label>
                  <Form.Control
                    type="text"
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    placeholder="{FirstName} — welcome to Connect"
                  />
                </Form.Group>

                <Form.Group className="mb-0">
                  <Form.Label className="fw-semibold">Email body</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={14}
                    value={emailBodyHtml}
                    onChange={(e) => setEmailBodyHtml(e.target.value)}
                    placeholder={"Hi {FirstName},\n\n..."}
                  />
                  <Form.Text className="text-muted">
                    Type the subject and body here. They are saved and used when the welcome email is sent. Tokens: {"{FirstName}"}, {"{LastName}"}, {"{FullName}"}, {"{MembershipName}"}, {"{LoginUrl}"}.
                  </Form.Text>
                </Form.Group>
              </Card.Body>
            </Card>
          </Form>
        </Modal.Body>
        <Modal.Footer className="bg-white border-top">
          <Button variant="outline-secondary" onClick={closeConfig} disabled={updateMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={
              updateMutation.isPending || !emailSubject.trim() || !emailBodyHtml.trim()
            }
            className="d-inline-flex align-items-center gap-2 px-4 bg-project-gradient border-0"
          >
            {updateMutation.isPending ? (
              <>
                <Spinner animation="border" size="sm" />
                Saving…
              </>
            ) : (
              "Save"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ManageWelcomeEmails;
